import { LeadObservationField } from "../lead-observation-editor";
import { WhatsAppMicrophoneDialog } from "./whatsapp-microphone-dialog";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@organizacaox/lai-design-system";
import { PhoneIcon, PhoneOffIcon } from "@organizacaox/lai-design-system/icons";
import { useQuery, useMutation } from "@organizacaox/lai-design-system/query";
import {
  getWhatsAppIncoming,
  rejectWhatsAppIncoming,
  type WhatsAppIncoming,
} from "@/api/whatsapp-call";
import { getLeadByPhone, getLeadDados } from "@/api/leads";
import { leadDadosRows } from "@/lead-dados";
import { useSession, useActiveOrganization } from "@/lib/auth-client";
import { useAccessSnapshot } from "@/hooks/use-viewer-role";
import { useWhatsAppVoice } from "./use-whatsapp-voice";
import { useIncomingRingtone } from "./use-incoming-ringtone";

export function WhatsAppIncomingCalls() {
  const { data: session } = useSession();
  const { data: org } = useActiveOrganization();
  const { permissions } = useAccessSnapshot();
  if (!org?.id || !session?.user.id || !permissions.has("dashboard.dialer.dial")) return null;
  return (
    <IncomingCalls
      key={`${org.id}:${session.user.id}`}
      organizationId={org.id}
      userId={session.user.id}
    />
  );
}

function IncomingCalls({ organizationId, userId }: { organizationId: string; userId: string }) {
  const { t } = useTranslation();
  const voice = useWhatsAppVoice(organizationId);
  const [selected, setSelected] = useState<WhatsAppIncoming | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const incoming = useQuery({
    queryKey: ["whatsapp-incoming", organizationId, userId],
    queryFn: ({ signal }) => getWhatsAppIncoming(signal),
    refetchInterval: 2000,
    refetchIntervalInBackground: true,
    retry: false,
  });
  const pending =
    !incoming.isError &&
    incoming.data?.call?.id !== dismissed &&
    incoming.data?.call?.id !== voice.endedCallId
      ? incoming.data?.call
      : null;
  const ringing = !!pending && !voice.active;
  const ringtone = useIncomingRingtone(ringing);
  const reject = useMutation({
    mutationFn: rejectWhatsAppIncoming,
    onSuccess: (_data, id) => {
      setDismissed(id);
      void incoming.refetch();
    },
  });
  const displayed = voice.active
    ? selected
    : pending || (voice.error && selected?.id !== dismissed ? selected : null);
  const error =
    (selected?.id === displayed?.id ? voice.error : null) ||
    (reject.isError ? reject.error.message : null);
  return (
    <>
      <Dialog open={!!displayed} onOpenChange={() => {}}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-xl"
        >
          <div className="mx-auto flex size-20 items-center justify-center rounded-full bg-primary/10 text-primary">
            <PhoneIcon className="size-9" />
          </div>
          <DialogHeader>
            <DialogTitle>
              {t(voice.active ? "whatsappCall.incoming.active" : "whatsappCall.incoming.title")}
            </DialogTitle>
            <DialogDescription>
              {displayed?.number ? `+${displayed.number}` : t("whatsappCall.incoming.unknown")}
            </DialogDescription>
          </DialogHeader>
          {displayed && (
            <IncomingLeadInfo
              number={displayed.number}
              organizationId={organizationId}
              userId={userId}
            />
          )}
          {voice.active && (
            <p role="status" className="text-center text-sm text-muted-foreground">
              {t(
                voice.state === "starting"
                  ? "whatsappCall.voice.states.starting"
                  : voice.state === "connected"
                    ? "whatsappCall.voice.states.connected"
                    : "whatsappCall.incoming.answering",
              )}
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {t(`whatsappCall.voice.errors.${error}`, {
                defaultValue: t("whatsappCall.voice.errors.generic"),
              })}
            </p>
          )}
          {ringing && ringtone.blocked && (
            <Button variant="outline" onClick={ringtone.enable}>
              {t("whatsappCall.incoming.enableSound")}
            </Button>
          )}
          <DialogFooter className="grid grid-cols-2">
            {!voice.active && !pending ? (
              <Button className="col-span-2" onClick={() => setSelected(null)}>
                {t("whatsappCall.incoming.dismiss")}
              </Button>
            ) : voice.active ? (
              <>
                <Button variant="outline" onClick={voice.toggleMute}>
                  {t(voice.muted ? "whatsappCall.voice.unmute" : "whatsappCall.voice.mute")}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (selected) setDismissed(selected.id);
                    voice.hangup();
                  }}
                >
                  <PhoneOffIcon className="size-4" />
                  {t("whatsappCall.voice.hangup")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  disabled={reject.isPending}
                  onClick={() => {
                    if (pending) reject.mutate(pending.id);
                  }}
                >
                  <PhoneOffIcon className="size-4" />
                  {t("whatsappCall.incoming.reject")}
                </Button>
                <Button
                  disabled={reject.isPending}
                  onClick={() => {
                    if (pending) {
                      setSelected(pending);
                      void voice.answer(pending.id);
                    }
                  }}
                >
                  <PhoneIcon className="size-4" />
                  {t("whatsappCall.incoming.answer")}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <WhatsAppMicrophoneDialog
        open={voice.microphoneNoticeOpen}
        onClose={voice.dismissMicrophoneNotice}
      />
    </>
  );
}

function IncomingLeadInfo({
  number,
  organizationId,
  userId,
}: {
  number: string | null;
  organizationId: string;
  userId: string;
}) {
  const { t } = useTranslation();
  const leadQuery = useQuery({
    queryKey: ["whatsapp-incoming-lead", organizationId, userId, number],
    queryFn: () => getLeadByPhone(number!),
    enabled: !!number,
    retry: false,
    staleTime: 30_000,
  });
  const lead = leadQuery.isError ? null : leadQuery.data;
  const details = useQuery({
    queryKey: ["whatsapp-incoming-lead-details", organizationId, userId, lead?.id],
    queryFn: () => getLeadDados(lead!.id),
    enabled: !!lead,
    retry: false,
    staleTime: 300_000,
  });
  const crmRows = leadDadosRows(details.isError ? undefined : details.data?.dados);
  const rows = [
    ...(lead?.email
      ? [{ key: "email", label: t("whatsappCall.incoming.email"), value: lead.email }]
      : []),
    ...(lead?.stage
      ? [{ key: "etapa", label: t("whatsappCall.incoming.stage"), value: lead.stage }]
      : []),
    ...(lead?.assigneeName
      ? [{ key: "owner", label: t("whatsappCall.incoming.owner"), value: lead.assigneeName }]
      : []),
    ...crmRows
      .filter((row) => !["email", "etapa", "observacao_negocio"].includes(row.key))
      .slice(0, 6),
  ];
  return (
    <section
      aria-label={t("whatsappCall.incoming.leadInfo")}
      className="max-h-[45dvh] overflow-y-auto rounded-xl border bg-muted/30 p-5"
    >
      {number && leadQuery.isPending ? (
        <div aria-label={t("whatsappCall.incoming.loadingLead")} className="space-y-3">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : lead ? (
        <>
          <p className="text-xs font-medium text-muted-foreground">
            {t("whatsappCall.incoming.leadInfo")}
          </p>
          <h3 className="mt-1 break-words text-2xl font-semibold tracking-tight">
            {lead.name || t("whatsappCall.incoming.unnamedLead")}
          </h3>
          {rows.length > 0 && (
            <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
              {rows.map((row) => (
                <div
                  key={row.key}
                  className={row.key === "observacao_negocio" ? "min-w-0 sm:col-span-2" : "min-w-0"}
                >
                  <dt className="text-xs text-muted-foreground">{row.label}</dt>
                  <dd className="mt-1 whitespace-pre-wrap break-words text-sm font-medium">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <div className="mt-4">
            <LeadObservationField
              leadId={lead.id}
              value={details.isError ? undefined : details.data?.dados.observacao_negocio}
              editable={!details.isError && details.data?.canEditObservation === true}
            />
          </div>
          {lead.hubspotUrl && (
            <Button
              variant="outline"
              className="mt-5"
              nativeButton={false}
              role="link"
              render={<a href={lead.hubspotUrl} target="_blank" rel="noopener noreferrer" />}
            >
              {t("whatsappCall.incoming.openCrm")}
            </Button>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t(
            leadQuery.isError
              ? "whatsappCall.incoming.leadUnavailable"
              : "whatsappCall.incoming.noLead",
          )}
        </p>
      )}
    </section>
  );
}
